import React, {
  ElementType,
  FC,
  ReactNode,
  useLayoutEffect,
  useState,
  useContext,
} from 'react';
import styled, { css, keyframes } from 'styled-components';

import { IconButton } from '../IconButton';
import { SidepanelContext } from './SidepanelContext';

interface SidepanelProps {
  /**
   * Element in the very left of the header.
   */
  initialLeftHeaderElement?: ReactNode;
  /**
   * What sidepanel should initially display in main content area
   */
  initialPanelContent?: ReactNode;
  /**
   * Provides a way to control footer children rendering from the outside
   */
  centerHeaderElement?: ReactNode;
  /**
   * Common "render" patter to control footer components rendering from the outside
   */
  renderFooter?: ElementType<{ isPanelCollapsed: boolean }>;
}

const triggerTestId = 'sidepanel-header-trigger';
const goBackTestId = 'sidepanel-header-back';

export const testIds = {
  triggerTestId,
  goBackTestId,
};

export const Sidepanel: FC<SidepanelProps> = ({
  initialLeftHeaderElement,
  initialPanelContent,
  centerHeaderElement,
  renderFooter: Footer,
  children,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [panelContent, setPanelContent] = useState([initialPanelContent]);
  const [leftHeaderElement, setLeftHeaderElement] = useState(
    initialLeftHeaderElement,
  );

  useLayoutEffect(() => {
    const deviceWidth = window.screen.width;

    if (deviceWidth <= 1024) {
      setIsCollapsed(true);
    }
  }, []);

  function pushContent(newContent: ReactNode) {
    const newPanelContent = [...panelContent, newContent];

    setPanelContent(newPanelContent);
    setLeftHeaderElement(<GoBackButton />);
  }

  function popContent() {
    const newPanelChildren =
      panelContent.length > 1 ? panelContent.slice(0, -1) : panelContent;

    setPanelContent(newPanelChildren);

    if (newPanelChildren.length <= 1) {
      setLeftHeaderElement(null);
    }
  }

  function setInitialPanelCollapsed(): void {
    setIsCollapsed(true);
  }

  return (
    <SidepanelWrapper>
      <SidepanelContext.Provider
        value={{
          pushContent,
          popContent,
          setLeftHeaderElement,
          setInitialPanelCollapsed,
        }}
      >
        <MobileHeader>
          <ToggleWrapper
            isInverted
            name="hamburger"
            testId={triggerTestId}
            onClick={() => setIsCollapsed(!isCollapsed)}
          />
        </MobileHeader>
        <Panel isCollapsed={isCollapsed}>
          <DesktopHeader isCollapsed={isCollapsed}>
            <HidableHeaderItems isInvisible={isCollapsed}>
              <LeftHeaderElement>{leftHeaderElement}</LeftHeaderElement>
              <CenterHeaderElement>{centerHeaderElement}</CenterHeaderElement>
            </HidableHeaderItems>
            <ToggleWrapper
              isInverted
              name="hamburger"
              testId={triggerTestId}
              onClick={() => setIsCollapsed(!isCollapsed)}
            />
          </DesktopHeader>
          <Main isInvisible={isCollapsed}>
            {panelContent.map((component, i) => (
              <Content key={i} isCurrent={i === panelContent.length - 1}>
                {component}
              </Content>
            ))}
          </Main>
          <FooterWrapper>
            {Footer && <Footer isPanelCollapsed={isCollapsed} />}
          </FooterWrapper>
        </Panel>
        <ContentWrapper id="sidepanel-content">{children}</ContentWrapper>
      </SidepanelContext.Provider>
    </SidepanelWrapper>
  );
};

const GoBackButton = () => {
  const context = useContext(SidepanelContext);

  return context ? (
    <IconButton
      isInverted
      name="chevronBack"
      testId={goBackTestId}
      onClick={context.popContent}
    />
  ) : null;
};

interface Collapsible {
  readonly isCollapsed?: boolean;
}

interface Hidable {
  readonly isInvisible: boolean;
}

const styledSlideIn = keyframes`
  0% {
    transform: translateX(-150%);
    opacity: 0;
  }
  100% {
    transform: translateX(0%);
    opacity: 1;
  }
`;

const styledSlideOut = keyframes`
  0% {
    transform: translateX(0%);
    opacity: 1;
  }
  100% {
    transform: translateX(-150%);
    opacity: 0;
  }
`;

const animation = css`
  animation: ${({ isInvisible }: Hidable) =>
      isInvisible ? styledSlideOut : styledSlideIn}
    500ms forwards;
`;

const SidepanelWrapper = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  z-index: 0;
  transform: translateX(0%);

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const ContentWrapper = styled.div`
  overflow: auto;
  /*
    Height is giving explicitly so when scrolling the content panel
    sticks to the left hand side and keeps being there
  */
  height: 100vh;
  flex: 2 2 auto;
  min-width: 0;
  position: relative;
  z-index: 0;

  @media (max-width: 1024px) {
    /*
      Page height minus Header height to stick the panel to the screen height
    */
    height: calc(100vh - 4em);
  }
`;

const Panel = styled.aside<Collapsible>`
  height: 100vh;
  display: flex;
  width: 22.5em;
  min-height: min-content;
  flex-direction: column;
  box-sizing: border-box;
  background-color: #1b1d25; /* TODO: Use default them with colours, sizes etc */
  box-sizing: border-box;
  z-index: 1;
  transition: all 500ms ease-out;

  @media (max-width: 1024px) {
    position: absolute;
    transform: ${({ isCollapsed }) =>
      isCollapsed ? 'translate(-100vw)' : 'translate(0)'};
    transition-property: transform;
  }

  @media (min-width: 1025px) {
    min-width: ${({ isCollapsed }) => (isCollapsed ? '4em' : '22.5em')};
    max-width: ${({ isCollapsed }) => (isCollapsed ? '4em' : '22.5em')};
    will-change: width, min-width, max-width;
    transition-property: max-width, min-width;
  }
`;

const Header = styled.header<Collapsible>`
  /*
    The height is to make sure that absolutely positioned side panel 
    in mobile view mode sticks to the screen height with no gaps
  */
  height: 4em;
  box-sizing: border-box;

  svg {
    font-size: 75%; /* Preserving current look&feel. TODO: confirm sizes with designer when there is one */
  }
`;

const MobileHeader = styled(Header)`
  display: none;
  background: #1b1d25;
  padding: 0.625em 1.25em;

  @media (max-width: 1024px) {
    display: flex;
  }
`;

const DesktopHeader = styled(Header)`
  display: flex;
  align-items: center;
  margin-bottom: 1.875em;
  padding: ${({ isCollapsed }) =>
    isCollapsed ? '0.25em 0.625em 0' : '0.25em 1.875em 0'};
  transition: padding 500ms ease-out;
  will-change: padding;
`;

const Main = styled.main<Hidable>`
  flex: 1;
  overflow: auto;
  padding: 0 1.875em;
  opacity: 1;

  ${animation}
`;

const FooterWrapper = styled.footer`
  display: flex;
  margin-top: 0.625em;
  padding: 0 1.875em;
`;

const LeftHeaderElement = styled.div``;

const CenterHeaderElement = styled.div`
  display: flex;
  align-items: center;
  max-width: 5em;
  height: 100%;
  margin: 0 auto;
`;

const HidableHeaderItems = styled.div<Hidable>`
  display: flex;
  height: 100%;
  flex: ${({ isInvisible }) => (isInvisible ? 0 : 1)};
  justify-content: space-between;
  align-items: center;
  overflow: hidden;

  ${animation}
`;

const Content = styled.div<{ isCurrent: boolean }>`
  display: ${({ isCurrent }) => (isCurrent ? 'block' : 'none')};
  overflow: visible;
`;

const ToggleWrapper = styled(IconButton)`
  margin-left: auto;

  @media (max-width: 1024px) {
    margin-left: 0;
    margin-right: auto;
  }
`;
